import { useParams, useNavigate } from 'react-router-dom';
import { blogPosts } from './blogData';
import { LazyImage } from '@/components/ui/lazy-image';
import Footer from '@/components/Footer';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useBreadcrumbJsonLd } from '@/hooks/useBreadcrumbJsonLd';
import { useArticleJsonLd } from '@/hooks/useArticleJsonLd';
import { ArrowLeft } from 'lucide-react';

const BlogPostPage = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();

    const post = blogPosts.find(
        (p) => p.slug === `/blog/${slug}`,
    );

    useDocumentMeta({
        title: post
            ? `${post.title} | Antz.ai Blog`
            : 'Post Not Found | Antz.ai Blog',
        description: post?.description ?? 'Blog post not found.',
        ogType: post ? 'article' : undefined,
        ogImage: post?.image,
        articlePublishedTime: post?.createdAt,
        articleAuthor: post?.author,
        articleTags: post?.tags,
    });

    useBreadcrumbJsonLd([
        { name: 'Home', href: '/' },
        { name: 'Blog', href: '/blog' },
        ...(post ? [{ name: post.title, href: post.slug }] : []),
    ]);

    useArticleJsonLd({
        title: post?.title ?? '',
        description: post?.description ?? '',
        slug: post?.slug ?? '',
        image: post?.image ?? '',
        datePublished: post?.createdAt ?? '',
        authorName: post?.author ?? '',
        tags: post?.tags,
    });

    if (!post || !post.content) {
        return (
            <div className="min-h-screen bg-background text-foreground">
                <div className="max-w-3xl mx-auto px-4 pt-32 pb-16 text-center">
                    <h1 className="text-3xl font-bold mb-4">
                        {post ? 'Coming Soon' : 'Post Not Found'}
                    </h1>
                    <p className="text-muted-foreground mb-8">
                        {post
                            ? 'This article is being written. Check back soon.'
                            : 'The blog post you are looking for does not exist.'}
                    </p>
                    <button
                        onClick={() => navigate('/blog')}
                        className="inline-flex items-center gap-2 text-primary hover:underline"
                    >
                        <ArrowLeft size={16} />
                        Back to Blog
                    </button>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            <article className="max-w-3xl mx-auto px-4 pt-28 pb-16">
                {/* Back link */}
                <button
                    onClick={() => navigate('/blog')}
                    className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
                >
                    <ArrowLeft size={16} />
                    Back to Blog
                </button>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.map((tag) => (
                        <span
                            key={tag}
                            className="text-xs px-2.5 py-1 rounded-full bg-accent text-accent-foreground"
                        >
                            {tag}
                        </span>
                    ))}
                </div>

                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 leading-tight">
                    {post.title}
                </h1>

                {/* Meta */}
                <div className="text-muted-foreground flex items-center gap-2 text-sm mb-8">
                    <p>by {post.author}</p>
                    <div className="bg-muted-foreground size-1 rounded-full" />
                    <p>{post.createdAt}</p>
                    <div className="bg-muted-foreground size-1 rounded-full" />
                    <p>{post.readTime}</p>
                </div>

                {/* Hero image */}
                <div className="mb-10">
                    <LazyImage
                        src={post.image}
                        fallback="https://placehold.co/960x540?text=antz.ai"
                        inView={false}
                        alt={post.title}
                        ratio={16 / 9}
                    />
                </div>

                {/* Content */}
                <div className="prose prose-neutral dark:prose-invert max-w-none prose-headings:tracking-tight prose-h2:text-2xl prose-h3:text-xl prose-p:leading-relaxed prose-p:text-[15px] prose-li:text-[15px] prose-strong:text-foreground">
                    <MarkdownContent content={post.content} />
                </div>
            </article>
            <Footer />
        </div>
    );
};

/** Simple markdown-to-JSX renderer for blog content */
function MarkdownContent({ content }: { content: string }) {
    const lines = content.trim().split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        // H2
        if (line.startsWith('## ')) {
            elements.push(
                <h2 key={i} className="mt-10 mb-4">
                    {line.slice(3)}
                </h2>,
            );
            i++;
            continue;
        }

        // H3
        if (line.startsWith('### ')) {
            elements.push(
                <h3 key={i} className="mt-8 mb-3">
                    {line.slice(4)}
                </h3>,
            );
            i++;
            continue;
        }

        // Empty line
        if (line.trim() === '') {
            i++;
            continue;
        }

        // Bold paragraph line starting with **
        if (line.startsWith('**') && line.endsWith('**')) {
            elements.push(
                <p key={i} className="font-semibold mt-4 mb-2">
                    {line.slice(2, -2)}
                </p>,
            );
            i++;
            continue;
        }

        // Regular paragraph — collect consecutive non-empty, non-heading lines
        const paragraphLines: string[] = [];
        while (
            i < lines.length &&
            lines[i].trim() !== '' &&
            !lines[i].startsWith('#')
        ) {
            paragraphLines.push(lines[i]);
            i++;
        }

        if (paragraphLines.length > 0) {
            const text = paragraphLines.join(' ');
            elements.push(
                <p key={`p-${i}`}>
                    {renderInlineFormatting(text)}
                </p>,
            );
        }
    }

    return <>{elements}</>;
}

/** Render inline formatting: **bold** and [links](url) */
function renderInlineFormatting(text: string): React.ReactNode[] {
    const parts: React.ReactNode[] = [];
    // Matches **bold** or [link text](url)
    const regex = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }

        if (match[1]) {
            // Bold
            parts.push(
                <strong key={match.index}>{match[1]}</strong>,
            );
        } else if (match[2] && match[3]) {
            // Link
            const isExternal = match[3].startsWith('http');
            parts.push(
                <a
                    key={match.index}
                    href={match[3]}
                    className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors"
                    {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                    {match[2]}
                </a>,
            );
        }

        lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts;
}

export default BlogPostPage;
